/**
 * <HealthGate> — pings `/actuator/health` and only renders its
 * children once the backend has answered 2xx.
 *
 * States (discriminated by the local state machine):
 *   checking   – first 800ms; render a minimal pulsing logo so the
 *                page doesn't flash a "loading" message for fast
 *                connections.
 *   slow       – 800ms..8s with no response yet; render a rotating
 *                "we're getting things ready" message that cycles
 *                every 2.2s. Cross-fades in/out (or just fades, under
 *                prefers-reduced-motion).
 *   dead       – any non-2xx, or no response after 8s. Render a
 *                "Server unavailable" card with a retry button.
 *   available  – a 200 came back. Render children with a soft
 *                fade-in (or instant, under prefers-reduced-motion).
 *
 * Uses an AbortController so unmount cancels the in-flight fetch.
 *
 * Goes straight to `fetch` instead of `apiClient.request` — we don't
 * want the global 401 → refresh dance during health probing, and
 * `/actuator/health` doesn't live behind a session anyway.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { Button } from '@vollgas/shared/ui'
import { apiUrl } from '@/apis/apiClient'
import { useI18n } from '@/i18n/TranslationContext'

const CHECKING_TO_SLOW_MS = 800
const SLOW_TO_DEAD_MS = 8_000
const MESSAGE_ROTATE_MS = 2_200

type GateState = 'checking' | 'slow' | 'dead' | 'available'

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false
  if (typeof window.matchMedia !== 'function') return false
  const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
  return Boolean(mq?.matches)
}

interface HealthGateProps {
  readonly children: ReactNode
}

export function HealthGate({ children }: HealthGateProps) {
  const { t } = useI18n()
  const [state, setState] = useState<GateState>('checking')
  const [attempt, setAttempt] = useState(0)
  const [messageIndex, setMessageIndex] = useState(0)

  // Refs so we can clear timers from any branch.
  const slowTimerRef = useRef<number | null>(null)
  const deadTimerRef = useRef<number | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  const clearTimers = useCallback(() => {
    if (slowTimerRef.current !== null) {
      window.clearTimeout(slowTimerRef.current)
      slowTimerRef.current = null
    }
    if (deadTimerRef.current !== null) {
      window.clearTimeout(deadTimerRef.current)
      deadTimerRef.current = null
    }
  }, [])

  const retry = useCallback(() => {
    abortRef.current?.abort()
    clearTimers()
    setMessageIndex(0)
    setState('checking')
    setAttempt((n) => n + 1)
  }, [clearTimers])

  // The probe itself — re-runs on every `attempt` bump.
  useEffect(() => {
    const ctl = new AbortController()
    abortRef.current = ctl

    slowTimerRef.current = window.setTimeout(() => {
      setState((s) => (s === 'checking' ? 'slow' : s))
    }, CHECKING_TO_SLOW_MS)

    deadTimerRef.current = window.setTimeout(() => {
      // Hard 8s ceiling — cancel the fetch, flip to dead.
      abortRef.current?.abort()
      setState((s) => (s === 'available' ? s : 'dead'))
    }, SLOW_TO_DEAD_MS)

    void (async () => {
      try {
        const res = await fetch(apiUrl('/actuator/health'), {
          credentials: 'include',
          signal: ctl.signal,
        })
        if (ctl.signal.aborted) return
        if (res.ok) {
          setState('available')
        } else {
          setState('dead')
        }
      } catch {
        if (ctl.signal.aborted) return
        setState('dead')
      } finally {
        clearTimers()
      }
    })()

    return () => {
      ctl.abort()
      clearTimers()
    }
  }, [attempt, clearTimers])

  // Rotating message — only ticks while we're in the `slow` state.
  useEffect(() => {
    if (state !== 'slow') return
    const messages = t.healthGate.slow
    if (messages.length <= 1) return
    const id = window.setInterval(() => {
      setMessageIndex((i) => (i + 1) % messages.length)
    }, MESSAGE_ROTATE_MS)
    return () => window.clearInterval(id)
  }, [state, t.healthGate.slow])

  if (state === 'available') {
    return prefersReducedMotion() ? <>{children}</> : <div className="animate-login-reveal">{children}</div>
  }

  if (state === 'dead') {
    return (
      <main className="mx-auto flex min-h-screen w-full max-w-[420px] flex-col items-stretch justify-center px-6 py-10">
        <div
          role="alert"
          className="flex flex-col items-stretch gap-4 rounded-md border border-glass-border bg-glass-fill p-6 text-center"
        >
          <img src="/Red-Just-Logo.png" alt="Vollgas" className="mx-auto h-[36px] w-auto" width={78} height={36} />
          <h1 className="font-display text-22 font-extrabold tracking-mk-neg5 text-glass-text">
            {t.healthGate.serverUnavailable}
          </h1>
          <p className="text-13 text-glass-text-muted">{t.healthGate.serverUnavailableHint}</p>
          <Button
            variant="primary"
            size="lg"
            onClick={retry}
            className="mt-1"
          >
            {t.healthGate.retry}
          </Button>
        </div>
      </main>
    )
  }

  // checking + slow share the centered-logo layout. The `slow` branch
  // adds a rotating message under the logo; `checking` is silent so
  // we don't flash a message on fast connections.
  const messages = t.healthGate.slow
  const safeIndex = messages.length === 0 ? 0 : messageIndex % messages.length
  const currentMessage = messages[safeIndex] ?? ''
  const reduced = prefersReducedMotion()
  const messageAnimClass = reduced ? 'animate-gate-msg-in' : 'animate-gate-msg-in'

  return (
    <main
      role="status"
      aria-live="polite"
      aria-busy="true"
      data-state={state}
      className="mx-auto flex min-h-screen w-full max-w-[420px] flex-col items-center justify-center gap-6 px-6 py-10 text-center"
    >
      <img
        src="/Red-Just-Logo.png"
        alt="Vollgas"
        width={78}
        height={36}
        className={reduced ? 'h-[36px] w-auto opacity-90' : 'h-[36px] w-auto animate-pulse opacity-90'}
      />
      {state === 'slow' && (
        <p
          // `key={...}` forces a remount on each rotation so the
          // animation re-plays — that's our fade.
          key={`${attempt}-${safeIndex}`}
          className={`text-13 font-semibold text-glass-text-muted ${messageAnimClass}`}
          data-testid="health-gate-slow-message"
        >
          {currentMessage}
        </p>
      )}
    </main>
  )
}
